import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';

// Icons
import CalendarPlusIcon from '@icons/CalendarPlusIcon';
import FavoriteIcon from '@icons/FavoriteIcon';
import MailIcon from '@icons/MailIcon'; /* eslint-disable-line */

// Components
import CardChip from '../CardChip';
import MemberCallToAction from '../MemberCallToAction';
import { BodySmall, ButtonNoStyles, H4 } from '@styled';

// Helpers
import { hasNewProperties, isNewMember } from '@general/helpers/member.helpers';
import getFavoriteHook from '@hooks/getFavoriteHook';

// Functionality
import useIcscTracking from '@tracking/useIcscTracking';

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    margin-top: 0;
    padding-top: 0.25rem;
`;

const IconContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 16px;
`;

const MemberListItemRedesign = props => {
    const {
        // Member properties
        businessType,
        companyID,
        companyName,
        companyURL,
        createdAt,
        designations,
        displayLocation,
        firstName,
        fullName,
        graphID,
        id,
        lastName,
        meetingInvites,
        photoURL,
        status,
        title,
        url = '',

        // Everything else
        customStyle = {},
        favorited = () => {},
        hit,
        isFavorite,
        isLoggedIn = true, // Replace with useAuthorized
        onCalendarClick,
        onClick = () => {},
        onMessageSent = () => {},
        onTrashClick,
        showCalendarIcon = false,
        showFavoriteIcon = true,
        showMailIcon = true,
        showTrashIcon = false,
        unfavorited,
        showCTA = true,
    } = props;

    // -----------------------------
    // State
    // -----------------------------

    const history = useHistory();
    const { trackEvent } = useIcscTracking();
    const useFavorite = getFavoriteHook({ type: 'contact', isFavorite });
    const { isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();
    const [isRemoved, setIsRemoved] = useState(false);

    // -----------------------------
    // Helpers
    // -----------------------------

    const onFavorite = async () => {
        if (!isFavorite || isRemoved) {
            setIsRemoved(false);
            saveFavorite(id);
            favorited(props);
            return;
        }

        if (isFavorite) {
            setIsRemoved(true);
            await removeFavorite(id);

            if (unfavorited) {
                unfavorited(id);
            }
        }
    };

    const onMessage = () => {
        onMessageSent(props);
        console.log(`Sent message to user : ${id}`);

        trackEvent({
            type: 'click',
            target: 'send_a_message_modal_mail_icon_button',
            value: `Messaged ${firstName} ${lastName}`,
        });
    };

    // -----------------------------
    // Render
    // -----------------------------

    return (
        <div className="list-card py-4 px-2 px-md-0 my-0 w-100" style={{ ...customStyle }}>
            <div className="d-flex">
                {/*
                <ImageContainer className="my-0 pt-1">
                    {photoURL && <img src={photoURL} alt={fullName} className="p-0 card-img-right profile-img-list" />}
                    {!photoURL && (
                        <div
                            className="p-0 member-default card-img-right profile-img-list d-flex align-items-center"
                            alt={fullName}
                        >
                            <div className="letters mx-auto">
                                <span className="display7">{firstName.slice(0, 1)}</span>
                                <span className="display7">{lastName.slice(0, 1)}</span>
                            </div>
                        </div>
                    )}
                </ImageContainer>
                */}

                <div className="d-flex w-100">
                    <div className="card-block m-0 p-0">
                        <H4 className="card-title mt-1 mb-0 ml-0 mx-0 py-0 display8 bold d-inline-block">
                            <a
                                className="d-inline-block align-middle mr-2"
                                href={`/member/profile/${id}`}
                                onClick={e => {
                                    onClick();

                                    // History is undefined if called from within a modal
                                    if (!history) {
                                        return;
                                    }

                                    e.preventDefault();
                                    history.push(`/member/profile/${id}`, { graphID });
                                }}
                            >
                                {fullName}
                                {designations && <span>{`, ${designations}`}</span>}
                            </a>
                        </H4>

                        {isNewMember(createdAt) && !hasNewProperties() && (
                            <div className="d-inline-block align-middle">
                                <CardChip>New Connection</CardChip>
                            </div>
                        )}

                        {hasNewProperties() && !isNewMember(createdAt) && (
                            <div className="d-inline-block align-middle">
                                <CardChip>New Property</CardChip>
                            </div>
                        )}

                        <div className="secondary mt-1 mb-0">
                            <BodySmall>{title}</BodySmall>
                        </div>

                        <div className="secondary mt-1 mb-0">
                            <BodySmall>{displayLocation}</BodySmall>
                        </div>

                        {(companyID || companyName) && (
                            <a
                                href={`/company/profile/${companyID}`}
                                className="d-inline-block text-gray-dark"
                                onClick={e => {
                                    onClick();

                                    // History is undefined if called from within a modal
                                    if (!history) {
                                        return;
                                    }

                                    e.preventDefault();
                                    history.push(`/company/profile/${companyID}`, { state: { inApp: true } });
                                }}
                            >
                                <div className="secondary mt-1">
                                    <BodySmall>
                                        {companyName +
                                            (businessType && businessType.length > 0 ? ` (${businessType[0]})` : '')}
                                    </BodySmall>
                                </div>
                            </a>
                        )}

                        {showCTA && (
                            <div className="mt-2">
                                <MemberCallToAction id={id} graphID={graphID} onClick={() => onClick(props)} />
                            </div>
                        )}
                    </div>

                    <IconContainer>
                        <div className="d-flex">
                            {showCalendarIcon && meetingInvites && (
                                <div className="mr-1.5rem">
                                    <ButtonNoStyles
                                        type="button"
                                        onClick={() => {
                                            onCalendarClick({
                                                id,
                                                graphID,
                                                firstName,
                                                lastName,
                                                fullName,
                                                companyID,
                                                companyName,
                                                title,
                                                designations,
                                                displayLocation,
                                                businessType,
                                                photoURL,
                                                url,
                                                companyURL,
                                                createdAt,
                                                isFavorite,
                                                objectID: hit.objectID,
                                                __queryID: hit.__queryID,
                                            });
                                        }}
                                    >
                                        <CalendarPlusIcon />
                                    </ButtonNoStyles>
                                </div>
                            )}

                            {showMailIcon && (
                                <MailIcon
                                    id={id}
                                    graphID={graphID}
                                    companyID={companyID}
                                    companyName={companyName}
                                    title={title}
                                    fullName={fullName}
                                    firstName={firstName}
                                    lastName={lastName}
                                    designations={designations}
                                    displayLocation={displayLocation}
                                    businessType={businessType}
                                    isFavorite={isFavorite}
                                    isLoggedIn={isLoggedIn}
                                    photoURL={photoURL}
                                    onMessage={onMessage}
                                    status={status}
                                />
                            )}

                            {showFavoriteIcon && (
                                <div>
                                    <FavoriteIcon
                                        id={id}
                                        type="member"
                                        isLoggedIn={isLoggedIn}
                                        isFavorite={isFavorite}
                                        onClick={onFavorite}
                                        isLoading={isLoadingFavorite}
                                    />
                                </div>
                            )}
                        </div>
                    </IconContainer>
                </div>
            </div>
        </div>
    );
};

MemberListItemRedesign.propTypes = {
    // Member properties
    businessType: PropTypes.array,
    companyID: PropTypes.number,
    companyName: PropTypes.string,
    companyURL: PropTypes.string,
    createdAt: PropTypes.string,
    designations: PropTypes.string.isRequired,
    displayLocation: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    graphID: PropTypes.string,
    id: PropTypes.number.isRequired,
    lastName: PropTypes.string.isRequired,
    meetingInvites: PropTypes.bool,
    photoURL: PropTypes.string,
    status: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,

    // Everything else
    customStyle: PropTypes.object,
    favorited: PropTypes.func,
    hit: PropTypes.object,
    isFavorite: PropTypes.bool.isRequired,
    isLoggedIn: PropTypes.bool,
    onCalendarClick: PropTypes.func,
    onClick: PropTypes.func,
    onMessageSent: PropTypes.func,
    onTrashClick: PropTypes.func,
    showCalendarIcon: PropTypes.bool,
    showFavoriteIcon: PropTypes.bool,
    showMailIcon: PropTypes.bool,
    showTrashIcon: PropTypes.bool,
    unfavorited: PropTypes.func,
    showCTA: PropTypes.bool,
};

export default MemberListItemRedesign;
